import type { LeaderboardService, SubmitRunOutcome } from "./LeaderboardService";
import { RunQueue } from "./RunQueue";
import type { LevelLeaderboardEntry, MyLevelRank, MyWorldRank, RunSubmission, ServiceResult, WorldLeaderboardEntry } from "./types";

const OFFLINE: ServiceResult<never> = { state: "offline" };

/**
 * Selected whenever Online Features are disabled (including "never
 * asked yet"). Makes zero network calls and never touches Supabase -
 * every read resolves to `{ state: "offline" }` synchronously (wrapped in
 * a resolved Promise only to match the interface), and nothing is ever
 * transmitted: choosing PLAY OFFLINE (or never being asked) means no
 * network call at all.
 *
 * PR34: `submitRun` still never sends anything, but a COMPLETED run is now
 * kept on this device (`RunQueue.captureWhileOffline()`, bounded to one
 * best result per level) instead of being discarded - so a player who
 * opts in later can still get the results they already earned onto the
 * leaderboard. The captured run only ever leaves the device through
 * `SupabaseLeaderboardService.flushQueue()`, which is itself gated on the
 * Online Features opt-in and a valid authenticated session. Failed runs
 * are never captured.
 */
class OfflineLeaderboardServiceImpl implements LeaderboardService {
  isAvailable(): boolean {
    return false;
  }

  async getLevelLeaderboard(): Promise<ServiceResult<LevelLeaderboardEntry[]>> {
    return OFFLINE;
  }

  async getMyLevelRank(): Promise<ServiceResult<MyLevelRank>> {
    return OFFLINE;
  }

  async getWorldLeaderboard(): Promise<ServiceResult<WorldLeaderboardEntry[]>> {
    return OFFLINE;
  }

  async getMyWorldRank(): Promise<ServiceResult<MyWorldRank>> {
    return OFFLINE;
  }

  async getMyDisplayName(): Promise<ServiceResult<string | null>> {
    return OFFLINE;
  }

  async setDisplayName(): Promise<ServiceResult<string>> {
    return OFFLINE;
  }

  async submitRun(run: RunSubmission): Promise<SubmitRunOutcome> {
    RunQueue.captureWhileOffline(run);
    return "disabled";
  }

  async flushQueue(): Promise<void> {
    // Deliberately a no-op: anything captured while Online Features are off
    // stays on this device until the player opts in.
  }
}

export const offlineLeaderboardService = new OfflineLeaderboardServiceImpl();
